"use client"

import { useState, useRef, useEffect } from 'react'
import { lazy, Suspense } from 'react'
import * as stylex from '@stylexjs/stylex'
import { useMediaContext } from '@/context/MediaContext'
import { VideoGalleryItem } from '@/models/gallery'
import { videoPageStyles } from '@/styles/VideoPage.stylex'

const VideoPopupRaw = lazy(() => import('@/components/popup/VideoPopup'));
const VideoPopup = (props: any) => (
    <Suspense fallback={null}>
        <VideoPopupRaw {...props} />
    </Suspense>
);

interface TextToVideoGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    items: VideoGalleryItem[]
}

export function TextToVideoGallery({ title, description, modelKey, modelLabel, modelNote, items }: TextToVideoGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<VideoGalleryItem>({ id: '', videoSrc: '', className: '', prompt: '', aspect_ratio: '' })
    const { registerMediaElement, unregisterMediaElement, onMediaPlay } = useMediaContext()
    const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map())

    // Register all video elements with MediaContext
    useEffect(() => {
        videoRefs.current.forEach((video, id) => {
            registerMediaElement(id, video)
        })
        return () => {
            videoRefs.current.forEach((_, id) => {
                unregisterMediaElement(id)
            })
        }
    }, [registerMediaElement, unregisterMediaElement, items])

    const handleVideoHover = (videoElement: HTMLVideoElement, videoId: string, isEntering: boolean) => {
        if (isEntering) {
            onMediaPlay(videoId)
            videoElement.muted = false
            videoElement.play().catch((error) => {
                if (error.name === 'AbortError') return
                videoElement.muted = true
                videoElement.play().catch(() => { })
            })
        } else {
            videoElement.pause()
            videoElement.muted = true
        }
    }

    const openPopup = (item: VideoGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    return (
        <>
            <div {...stylex.props(videoPageStyles.gallerySection)}>
                <h3>{title}</h3>
                <p dangerouslySetInnerHTML={{ __html: description }} />
                <div {...stylex.props(videoPageStyles.videoGalleryGrid)}>
                    {items.map((item, index) => (
                        <GalleryItem
                            key={item.id || index}
                            item={item}
                            index={index}
                            videoRefs={videoRefs}
                            onHover={handleVideoHover}
                            onClick={() => openPopup(item)}
                        />
                    ))}
                </div>
            </div>

            <VideoPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                id={selectedItem.id}
                videoSrc={selectedItem.videoSrc}
                thumbnailSrc={selectedItem.thumbnailSrc}
                modelKey={modelKey}
                modelLabel={modelLabel}
                prompt={selectedItem.prompt}
                aspect_ratio={selectedItem.aspect_ratio}
                popupTitle="Video Details"
                modelNote={modelNote}
            />
        </>
    )
}

/**
 * Sub-component to manage per-item hover states and dynamic layout classes
 */
function GalleryItem({ item, index, videoRefs, onHover, onClick }: any) {
    const [isHovered, setIsHovered] = useState(false);
    const videoId = `text-to-video-${item.id || index}`;

    // Map dynamic layout strings (featured, wide, tall) to StyleX objects
    const dynamicStyle = item.className === 'featured'
        ? videoPageStyles.featured
        : item.className === 'wide'
            ? videoPageStyles.wide
            : item.className === 'tall'
                ? videoPageStyles.tall
                : null;

    return (
        <div
            {...stylex.props(
                videoPageStyles.videoGalleryItem,
                dynamicStyle
            )}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div {...stylex.props(videoPageStyles.videoContainer)}>
                <video
                    ref={(el) => { if (el) videoRefs.current.set(videoId, el) }}
                    src={item.videoSrc}
                    poster={item.thumbnailSrc}
                    {...stylex.props(videoPageStyles.galleryVideo)}
                    preload="none"
                    loop
                    muted
                    playsInline
                    onMouseEnter={(e) => onHover(e.target as HTMLVideoElement, videoId, true)}
                    onMouseLeave={(e) => onHover(e.target as HTMLVideoElement, videoId, false)}
                />
            </div>

            {/* Click Indicator appears on hover via indicatorActive */}
            <div {...stylex.props(
                videoPageStyles.clickIndicator,
                isHovered && videoPageStyles.indicatorActive
            )}>
                Click me
            </div>
        </div>
    );
}